import React from 'react';
import { motion } from 'framer-motion';
import { User, Mail, ChevronRight } from 'lucide-react';

// Simple Card components
const Card = ({ className, children }) => (
  <div className={`rounded-xl border bg-card text-card-foreground overflow-hidden border-none shadow-md ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ className, children }) => (
  <div className={`bg-gradient-to-r from-[#F16745] to-[#F16745] text-white p-6 ${className}`}>
    {children}
  </div>
);

const CardContent = ({ className, children }) => (
  <div className={`p-6 space-y-6 ${className}`}>
    {children}
  </div>
);

const CardFooter = ({ className, children }) => (
  <div className={`flex justify-end p-6 pt-0 ${className}`}>
    {children}
  </div>
);

// Badge component
const Badge = ({ className, children }) => (
  <div className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${className}`}>
    {children}
  </div>
);

// Separator component
const Separator = ({ className }) => (
  <div className={`h-px bg-gray-200 my-2 ${className}`} />
);

const DeceasedUserCard = ({ userData, onViewFormData }) => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      className="max-w-3xl mx-auto"
    >
      <Card>
        <CardHeader>
          <div className=" flex justify-between items-center">
            <h2 className="text-2xl font-bold tracking-tight">
              Deceased User Information
            </h2>
            <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
              Memorial Record
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          <motion.div 
            className="bg-gradient-to-r from-[#F1EBDB]/50 to-[#F1EBDB]/30 p-6 rounded-xl border border-[#F1EBDB] shadow-sm"
            // whileHover={{ scale: 1.01 }}
            // transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[#F16745]/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-[#F16745]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{userData.user?.name}</h3>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    <span>{userData.user?.email}</span>
                  </div>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                // transition={{ type: "spring", stiffness: 300 }}
              >
                <Badge className="bg-[#F16745] hover:bg-[#F16745]/90 text-white">
                  Deceased User
                </Badge>
              </motion.div>
            </div>
          </motion.div>

          <Separator />
          
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">Personal Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div 
                className="bg-gray-50 p-5 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-start">
                  <div className="mr-3 mt-1 p-2 rounded-md bg-[#F16745]/10">
                    <User className="h-4 w-4 text-[#F16745]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Full Name</p>
                    <p className="text-gray-900 font-medium">
                      {userData.user?.name || 'Not available'}
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-gray-50 p-5 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-start">
                  <div className="mr-3 mt-1 p-2 rounded-md bg-[#F16745]/10">
                    <Mail className="h-4 w-4 text-[#F16745]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Email Address</p>
                    <p className="text-gray-900 font-medium">
                      {userData.user?.email || 'Not available'}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </CardContent>

        <CardFooter>
          <button 
            onClick={onViewFormData}
            className="bg-[#F16745] hover:bg-[#F16745]/90 text-white px-6 py-2.5 rounded-lg font-medium flex items-center group"
          >
            <span>View Form Data</span>
            <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default DeceasedUserCard; 